import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Customers from './pages/Customers/Customers';
import CustomerDetails from './pages/Customers/CustomerDetails';
import EditCustomer from './pages/Customers/EditCustomer';
import CreateCustomer from './pages/Customers/CreateCustomer';
import Loans from './pages/Loans/Loans';
import LoanDetails from './pages/Loans/LoanDetails';
import CreateLoan from './pages/Loans/CreateLoan';
import Payments from './pages/Payments/Payments';
import CreatePayment from './pages/Payments/CreatePayment';
import PaymentDetails from './pages/Payments/PaymentDetails';
import Reports from './pages/Reports/Reports';
import Users from './pages/Users/Users';
import Settings from './pages/Settings/Settings';
import MetalRates from './pages/Settings/MetalRates';
import { RatesProvider } from './contexts/RatesContext';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <RatesProvider>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          
          {/* Customer Routes */}
          <Route path="customers" element={<Customers />} />
          <Route path="customers/create" element={<CreateCustomer />} />
          <Route path="customers/:id" element={<CustomerDetails />} />
          <Route path="customers/:id/edit" element={<EditCustomer />} />
          
          {/* Loan Routes */}
          <Route path="loans" element={<Loans />} />
          <Route path="loans/create" element={<CreateLoan />} />
          <Route path="loans/:id" element={<LoanDetails />} />
          
          {/* Payment Routes */}
          <Route path="payments" element={<Payments />} />
          <Route path="payments/create" element={<CreatePayment />} />
          <Route path="payments/:id" element={<PaymentDetails />} />
          
          {/* Report Routes */}
          <Route path="reports" element={<Reports />} />
          
          {/* User Management Routes (secured by backend roles) */}
          <Route
            path="users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          
          {/* Settings Route */}
          <Route path="settings" element={<Settings />} />
          <Route path="settings/metal-rates" element={<MetalRates />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </RatesProvider>
    </div>
  );
}

export default App;
